import React, { useState } from 'react';
import { CheckCircle2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { Button } from '../components/ui/Button';
import { CtaBand } from '../components/ui/CtaBand';
const categories = ['General enquiries', 'Media', 'Volunteer', 'Events', 'Campaign office', 'Partnerships', 'Technical support'];
const offices = [{
  name: 'National campaign headquarters',
  address: 'Plot 14, Yakubu Gowon Crescent, Asokoro, Abuja',
  phone: '+234 800 227 3562',
  email: 'hello@bello2027.ng'
}, {
  name: 'South West regional office',
  address: '22 Adeola Odeku Street, Victoria Island, Lagos',
  phone: '+234 800 227 3563',
  email: 'southwest@bello2027.ng'
}, {
  name: 'North West regional office',
  address: '5 Ahmadu Bello Way, Nassarawa GRA, Kano',
  phone: '+234 800 227 3564',
  email: 'northwest@bello2027.ng'
}];
export function Contact() {
  const [category, setCategory] = useState(categories[0]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent'>('idle');
  const needsOrganisation = category === 'Media' || category === 'Partnerships';
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Enter a valid email address.';
    if (message.trim().length < 10) next.message = 'Please give us a little more detail.';
    if (needsOrganisation && !organisation.trim()) next.organisation = 'Enter your organisation.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('loading');
    window.setTimeout(() => setStatus('sent'), 900);
  };
  const inputClass = (hasError: boolean) => `w-full rounded-lg border bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-500/70 focus:outline-none ${hasError ? 'border-red-600' : 'border-sand-300 focus:border-green-600'}`;
  return <>
      <PageHero eyebrow="Contact" title="Talk to the campaign." intro="Every message is read. Media and accreditation requests are answered within one working day." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Contact'
    }]} data-id="element-699" data-mp="1r7rmv4rkf5i5" />

      <section className="bg-sand-50 py-14 lg:py-20" aria-label="Contact form and offices" data-id="element-700" data-mp="1d6f98zzvnfzf">
        <div className="mx-auto grid max-w-content gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-10" data-id="element-701" data-mp="18g8kn05d7vh6">
          <div className="lg:col-span-7" data-id="element-702" data-mp="kq4gmro9ltpn">
            {status === 'sent' ? <div role="status" className="flex items-start gap-4 rounded-media border border-green-200 bg-white p-8" data-id="element-703" data-mp="ko1ifkcyku9u">
                <CheckCircle2Icon aria-hidden className="mt-0.5 h-6 w-6 shrink-0 text-green-600" data-id="element-704" data-mp="2rd24ief5ulv5" />
                <div data-id="element-705" data-mp="2w6xy74oe5mki">
                  <h2 className="font-display text-[24px] text-ink" data-id="element-706" data-mp="2w7g3eqt17b4k">Message received.</h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-707" data-mp="2hia30zigjev3">
                    Thank you, {name.split(' ')[0]}. Your {category.toLowerCase()} message has been
                    routed to the right team. You will hear back at {email}.
                  </p>
                </div>
              </div> : <form onSubmit={onSubmit} noValidate className="rounded-media border border-sand-200 bg-white p-6 sm:p-9" data-id="element-708" data-mp="314pxwkva2cz9">
                <h2 className="font-display text-[24px] text-ink" data-id="element-709" data-mp="1ppauay8kcmv6">Send a message</h2>

                <div className="mt-6" data-id="element-710" data-mp="1prnk1xkkc1is">
                  <label htmlFor="c-category" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-711" data-mp="1tr4qat7td2p0">
                    What is this about?
                  </label>
                  <select id="c-category" value={category} onChange={event => setCategory(event.target.value)} className={inputClass(false)} data-id="element-712" data-mp="257aq8s65b8ug">
                    {categories.map(item => <option key={item} value={item} data-id="element-713" data-mp="1v7a46ygw6emk">
                        {item}
                      </option>)}
                  </select>
                </div>

                <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-714" data-mp="2nvpfpb0teet5">
                  <div data-id="element-715" data-mp="2ixe691efm6bg">
                    <label htmlFor="c-name" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-716" data-mp="2iww4xqcwx1wo">
                      Full name <span className="text-red-600" data-id="element-717" data-mp="2ezgh1yknvk82">*</span>
                    </label>
                    <input id="c-name" autoComplete="name" value={name} onChange={event => setName(event.target.value)} aria-invalid={Boolean(errors.name)} className={inputClass(Boolean(errors.name))} data-id="element-718" data-mp="17hfs4j4ecu3d" />
                    {errors.name && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-719" data-mp="3o571efh6e3ov">{errors.name}</p>}
                  </div>
                  <div data-id="element-720" data-mp="3sy9wis1lpl76">
                    <label htmlFor="c-email" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-721" data-mp="3syrxu334eply">
                      Email <span className="text-red-600" data-id="element-722" data-mp="1sqoq02pu62y">*</span>
                    </label>
                    <input id="c-email" type="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} aria-invalid={Boolean(errors.email)} className={inputClass(Boolean(errors.email))} data-id="element-723" data-mp="6rlbte7pugsv" />
                    {errors.email && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-724" data-mp="3krxtej357l8h">{errors.email}</p>}
                  </div>
                  {needsOrganisation && <div className="sm:col-span-2" data-id="element-725" data-mp="3tt4buxpcr64d">
                      <label htmlFor="c-org" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-726" data-mp="3ttxblepw8vd0">
                        Organisation <span className="text-red-600" data-id="element-727" data-mp="7kw1x3ru2gc5">*</span>
                      </label>
                      <input id="c-org" value={organisation} onChange={event => setOrganisation(event.target.value)} aria-invalid={Boolean(errors.organisation)} className={inputClass(Boolean(errors.organisation))} data-id="element-728" data-mp="2nmajikvvulo" />
                      {errors.organisation && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-729" data-mp="lenao9xqpw6t">{errors.organisation}</p>}
                    </div>}
                  <div className="sm:col-span-2" data-id="element-730" data-mp="19xxcgpcmn6dc">
                    <label htmlFor="c-message" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-731" data-mp="19xp2p6bswh6d">
                      Message <span className="text-red-600" data-id="element-732" data-mp="zyzu645so92s">*</span>
                    </label>
                    <textarea id="c-message" rows={6} value={message} onChange={event => setMessage(event.target.value)} aria-invalid={Boolean(errors.message)} className={inputClass(Boolean(errors.message))} data-id="element-733" data-mp="m8csc34qm32h" />
                    {errors.message && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-734" data-mp="3atgysut48cw">{errors.message}</p>}
                  </div>
                </div>

                <Button type="submit" size="lg" loading={status === 'loading'} className="mt-7 w-full sm:w-auto" data-id="element-735" data-mp="7d6ptq4uomdw">
                  {status === 'loading' ? 'Sending…' : 'Send message'}
                </Button>
              </form>}
          </div>

          <aside className="lg:col-span-5" data-id="element-736" data-mp="8skhvca4veyd">
            <ul className="space-y-5" data-id="element-737" data-mp="8vyhicrexa24">
              {offices.map(office => <li key={office.name} className="rounded-media border border-sand-200 bg-white p-6" data-id="element-738" data-mp="4tbnc579rd1r">
                  <h2 className="font-display text-[20px] text-ink" data-id="element-739" data-mp="3w4lf2hgnz9py">{office.name}</h2>
                  <ul className="mt-4 space-y-2.5 text-[15px] text-slate-600" data-id="element-740" data-mp="3r83b3jt5rfeo">
                    <li className="flex items-start gap-2.5" data-id="element-741" data-mp="3m9jtfvllkps4">
                      <MapPinIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-green-600" data-id="element-742" data-mp="3h95tl0u5gm56" />
                      {office.address}
                    </li>
                    <li className="flex items-center gap-2.5" data-id="element-743" data-mp="3d8luhrq0n8ge">
                      <PhoneIcon aria-hidden className="h-4 w-4 text-green-600" data-id="element-744" data-mp="1soq6ngohsj34" />
                      <a href={`tel:${office.phone}`} className="hover:text-green-700" data-id="element-745" data-mp="1wqtvd7069k6c">
                        {office.phone}
                      </a>
                    </li>
                    <li className="flex items-center gap-2.5" data-id="element-746" data-mp="26pj3i829nq9e">
                      <MailIcon aria-hidden className="h-4 w-4 text-green-600" data-id="element-747" data-mp="26p110tp0gft1" />
                      <a href={`mailto:${office.email}`} className="hover:text-green-700" data-id="element-748" data-mp="192txzo8cuvis">
                        {office.email}
                      </a>
                    </li>
                  </ul>
                </li>)}
            </ul>
          </aside>
        </div>
      </section>

      <CtaBand title="Looking for something faster?" description="Most answers are already published — including our responses to circulating claims." primary={{
      label: 'Read the FAQs',
      to: '/faq'
    }} secondary={{
      label: 'Facts & answers',
      to: '/facts'
    }} data-id="element-749" data-mp="1nrq95u5pkonm" />
    </>;
}